import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { ProgramNames } from '@lyrahealth-inc/shared-app-logic/src/models/programs/Programs'

import { ROLES } from '../../../common/constants/appConstants'
import { hasRole } from '../../../common/utils/utils'
import { getAuthRoles, getAuthUserCapacityValue } from '../../../data/auth/authSelectors'
import { useAllClientsData } from '../../clients/data/hooks/useAllClientsData'
import { useClientsInProgramData } from '../../clients/data/hooks/useClientsInProgramData'
import { mergeClientsById } from '../dashboardUtils'

export const useDashboardClientsData = () => {
  const { isDBTTeensEnabled } = useFlags()
  const userRoles = useSelector(getAuthRoles)
  const capacities = useSelector(getAuthUserCapacityValue)
  const isDBTTeensProvider =
    hasRole(userRoles, [ROLES.LT_DBT_TEENS_THERAPIST, ROLES.LT_DBT_TEENS_THERAPIST_SUPERVISOR]) ||
    Object.keys(capacities ?? {}).includes(ProgramNames.DialecticalBehaviorTherapyTeens)
  const includeInteractedDBTTeensClients = isDBTTeensEnabled && isDBTTeensProvider

  const [primaryClients, activeClientsQuery, inactiveClientsQuery] = useAllClientsData()
  // Use `interactionMode: 'interacted_with'` because `interactionMode: 'all'`
  // would return the full DBT Teens program roster. Primary clients already come from useAllClientsData,
  // so this query only needs clients who have interacted with this provider.
  const interactedDBTTeensClientsQuery = useClientsInProgramData(
    {
      programName: ProgramNames.DialecticalBehaviorTherapyTeens,
      interactionMode: 'interacted_with',
    },
    { skip: !includeInteractedDBTTeensClients },
  )

  const clients = useMemo(
    () => mergeClientsById(primaryClients, interactedDBTTeensClientsQuery.data),
    [interactedDBTTeensClientsQuery.data, primaryClients],
  )
  const activeAndInteractedClients = useMemo(
    () => mergeClientsById(activeClientsQuery.data, interactedDBTTeensClientsQuery.data),
    [activeClientsQuery.data, interactedDBTTeensClientsQuery.data],
  )

  const isLoading =
    activeClientsQuery.isLoading ||
    inactiveClientsQuery.isLoading ||
    (includeInteractedDBTTeensClients && interactedDBTTeensClientsQuery.isLoading)
  const isSuccess =
    activeClientsQuery.isSuccess &&
    inactiveClientsQuery.isSuccess &&
    (!includeInteractedDBTTeensClients || interactedDBTTeensClientsQuery.isSuccess)

  const refetch = () =>
    Promise.all([
      activeClientsQuery.refetch().unwrap(),
      inactiveClientsQuery.refetch().unwrap(),
      ...(includeInteractedDBTTeensClients ? [interactedDBTTeensClientsQuery.refetch().unwrap()] : []),
    ])

  return {
    clients,
    activeAndInteractedClients,
    includeInteractedDBTTeensClients,
    primaryClients,
    inactiveClients: inactiveClientsQuery.data,
    isLoading,
    isSuccess,
    refetch,
  }
}
